import { useEffect, useState } from 'react';

/** How long the flag must hold true before the UI reacts to it. */
export const SUSTAINED_FLAG_RISE_MS = 500;

/** How long it must hold false before the UI stops. */
export const SUSTAINED_FLAG_FALL_MS = 700;

/**
 * Absorbs a flag's dips: it counts as held until it has been false for
 * `SUSTAINED_FLAG_FALL_MS`, so an agent's tool loop dipping "busy" once per
 * cycle reads as one stretch. Rises are not delayed.
 *
 * @param isActive - The raw flag
 * @returns Whether the flag is held
 */
export function useHeldFlag(isActive: boolean): boolean {
  const [isHeld, setIsHeld] = useState(false);

  // eslint-disable-next-line no-restricted-syntax -- reason: absorbing the dips is a timed transition with no other source
  useEffect(() => {
    if (isActive) {
      setIsHeld(true);
      return undefined;
    }
    if (!isHeld) return undefined;
    const timer = setTimeout(() => setIsHeld(false), SUSTAINED_FLAG_FALL_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [isActive, isHeld]);

  return isHeld;
}

/**
 * Smooths a flag that flaps faster than a person can read.
 *
 * Two stages, because the edges answer different questions. The dips are
 * absorbed first ({@link useHeldFlag}). Only then is the rise delayed by
 * `SUSTAINED_FLAG_RISE_MS`, so work that resolves on its own flashes nothing.
 *
 * The order matters: delaying the rise first lets every dip reset that clock,
 * and a flag flapping faster than the delay would never raise at all.
 *
 * @param isActive - The raw flag
 * @returns Whether the UI should currently be showing it
 */
export function useSustainedFlag(isActive: boolean): boolean {
  const isHeld = useHeldFlag(isActive);
  const [isRaised, setIsRaised] = useState(false);

  // eslint-disable-next-line no-restricted-syntax -- reason: as above, for the rising edge
  useEffect(() => {
    if (!isHeld) {
      setIsRaised(false);
      return undefined;
    }
    if (isRaised) return undefined;
    const timer = setTimeout(() => setIsRaised(true), SUSTAINED_FLAG_RISE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [isHeld, isRaised]);

  return isRaised;
}
